import { dimensions, type ScoreVector } from '../data/dimensions';
import { matchWeights, regularTypes, type HiddenType, type PersonalityType, type RegularType } from '../data/taxonomy';
import { resolveHiddenType } from './patternDetectors';
import type { ScoreEventInput } from './scoring';

type MatchResult = {
  primary: RegularType;
  secondary?: RegularType;
  confidence: number;
  distance: number;
};

type PersonalityMatchResult = Omit<MatchResult, 'primary'> & {
  primary: PersonalityType;
  secondary?: RegularType;
  regularPrimary: RegularType;
  hidden?: HiddenType;
};

type RankedRegularType = {
  type: RegularType;
  distance: number;
};

const ambiguousConfidenceThreshold = 12;
const ambiguousDistanceBand = 18;
const ambiguousPoolLimit = 6;

function weightedDistance(a: ScoreVector, b: ScoreVector): number {
  const sum = dimensions.reduce((total, dimension) => {
    const diff = (a[dimension] - b[dimension]) * matchWeights[dimension];
    return total + diff * diff;
  }, 0);

  return Math.sqrt(sum);
}

function eventSignature(events: ScoreEventInput[]) {
  return events
    .map((event) => `${event.sceneId}:${event.coreEvent}:${event.secondaryChoiceId ?? 'none'}`)
    .join('|');
}

function hashSeed(seed: string) {
  let hash = 2166136261;
  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function resolveAmbiguousRegularMatch(ranked: RankedRegularType[], events: ScoreEventInput[]) {
  const first = ranked[0];
  const second = ranked[1];
  const confidence = second ? second.distance - first.distance : 999;
  if (!events.length || confidence >= ambiguousConfidenceThreshold) {
    return first;
  }

  const pool = ranked
    .filter((candidate) => candidate.distance - first.distance <= ambiguousDistanceBand)
    .slice(0, ambiguousPoolLimit);
  if (pool.length <= 1) {
    return first;
  }

  return pool[hashSeed(eventSignature(events)) % pool.length];
}

export function matchRegularType(scores: ScoreVector, events: ScoreEventInput[] = []): MatchResult {
  const ranked = regularTypes
    .map((type) => ({ type, distance: weightedDistance(scores, type.vector) }))
    .sort((a, b) => a.distance - b.distance);

  const first = resolveAmbiguousRegularMatch(ranked, events);
  const second = ranked.find((candidate) => candidate.type.typeId !== first.type.typeId);

  return {
    primary: first.type,
    secondary: second?.type,
    confidence: second ? Math.max(0, second.distance - first.distance) : 999,
    distance: first.distance
  };
}

export function matchPersonalityType(scores: ScoreVector, events: ScoreEventInput[] = []): PersonalityMatchResult {
  const regularMatch = matchRegularType(scores, events);
  const hidden = resolveHiddenType(scores, events);

  return {
    ...regularMatch,
    primary: hidden?.override ? hidden : regularMatch.primary,
    regularPrimary: regularMatch.primary,
    hidden
  };
}
